import { useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useSession } from "@/lib/session";
import { typeDeLien } from "@/lib/supabase";
import { Connexion } from "@/features/auth/Connexion";
import { MotDePasse } from "@/features/auth/MotDePasse";
import { Coquille } from "@/features/coquille/Coquille";
import { Referentiel } from "@/features/catalogue/Referentiel";
import { FicheProduit } from "@/features/catalogue/FicheProduit";
import { Ventes } from "@/features/ventes/Ventes";
import { FicheVente } from "@/features/ventes/FicheVente";
import { Commissions } from "@/features/commissions/Commissions";
import { Usines } from "@/features/usines/Usines";
import { Clients } from "@/features/clients/Clients";
import { FicheClient } from "@/features/clients/FicheClient";
import { FicheUsine } from "@/features/usines/FicheUsine";
import { Agents } from "@/features/agents/Agents";
import { FicheAgent } from "@/features/agents/FicheAgent";
import { Modeles } from "@/features/modeles/Modeles";
import { NouveauDevis } from "@/features/devis/NouveauDevis";
import { Facturation } from "@/features/facturation/Facturation";
import { Import } from "@/features/import/Import";
import { Prix } from "@/features/prix/Prix";
import { EspaceUsine } from "@/features/espace-usine/EspaceUsine";
import { Charte } from "@/features/charte/Charte";

export function App() {
  const { session, profil, chargement, deconnexion } = useSession();
  /* Arrivée par invitation ou par lien de réinitialisation : le mot de passe se
     pose avant tout le reste. Le lien ouvre la session, mais c'est le drapeau
     porté par le compte qui fait foi — un rechargement efface le fragment de
     l'adresse, pas le drapeau. */
  const [pose, setPose] = useState(false);
  const aPoser =
    !pose &&
    (typeDeLien === "invite" ||
      typeDeLien === "recovery" ||
      session?.user.user_metadata?.["mdp_pose"] !== true);

  if (chargement) {
    return (
      <div className="auth">
        <div className="chargement">Chargement…</div>
      </div>
    );
  }

  if (!session) return <Connexion />;

  if (aPoser) {
    return <MotDePasse invitation={typeDeLien !== "recovery"} onFait={() => setPose(true)} />;
  }

  /* Deux situations tombent ici et ce ne sont pas les mêmes : le compte qui
     n'a jamais eu de fiche, et celui dont la fiche a été suspendue. La base
     ne rend rien dans les deux cas, mais l'un attend une création et l'autre
     une réactivation — le dire évite un appel pour rien.

     Et dans les deux cas, une porte : sans bouton de sortie, celui qui se
     trompe de compte reste enfermé sur cet écran. */
  if (!profil) {
    return (
      <div className="auth">
        <div className="auth-carte">
          <h1 className="auth-titre">Compte sans accès</h1>
          <p className="auth-note">
            Ce compte existe, mais aucune fiche agent active ne lui est rattachée —
            soit elle n'a pas encore été créée, soit elle a été suspendue.
            La direction peut la poser ou la réactiver dans Agents.
          </p>
          <p className="auth-qui">
            Connecté en <b>{session.user.email}</b>
          </p>
          <button
            type="button"
            className="btn auth-entrer"
            onClick={() => void deconnexion()}
          >
            Se déconnecter
          </button>
        </div>
      </div>
    );
  }

  /* Un compte usine n'entre pas dans Omnix Desk : il a son propre espace, et
     la base lui refuse déjà tout le reste. L'écran ne fait que ne pas le
     proposer — les deux vont ensemble, et c'est le second qui est le confort. */
  if (profil.portee === "usine") return <EspaceUsine />;

  return (
    <Coquille>
      <Routes>
        <Route path="/" element={<Navigate to="/catalogue" replace />} />
        <Route path="/catalogue" element={<Referentiel />} />
        <Route path="/catalogue/listes" element={<Referentiel />} />
        <Route path="/catalogue/:id" element={<FicheProduit />} />
        {/* Ancien chemin : les liens déjà posés continuent de mener au bon endroit. */}
        <Route path="/listes" element={<Navigate to="/catalogue/listes" replace />} />
        <Route path="/ventes" element={<Ventes />} />
        <Route path="/ventes/:id" element={<FicheVente />} />
        <Route path="/commissions" element={<Commissions />} />
        <Route path="/usines" element={<Usines />} />
        <Route path="/usines/:id" element={<FicheUsine />} />
        <Route path="/clients" element={<Clients />} />
        <Route path="/clients/:id" element={<FicheClient />} />
        <Route path="/devis" element={<NouveauDevis />} />
        <Route path="/modeles" element={<Modeles />} />
        <Route path="/facturation" element={<Facturation />} />
        <Route path="/agents" element={<Agents />} />
        <Route path="/agents/:id" element={<FicheAgent />} />
        <Route path="/import" element={<Import />} />
        <Route path="/prix" element={<Prix />} />
        {/* La Charte n'est pas un écran agent : c'est l'outillage qui mesure le
            design system. Elle quitte le menu et garde sa route. */}
        <Route path="/charte" element={<Charte />} />
        {/* Les domaines sortis du périmètre V1 — Opportunités, Demandes de prix,
            Prix annoncés, Packaging, Qualité, Santé de la base, Pilotage — ne
            sont plus routés ici. Leur code vit dans `src/v2/`, les tables
            restent en base, et un lien déjà posé retombe sur le catalogue. */}
        <Route path="*" element={<Navigate to="/catalogue" replace />} />
      </Routes>
    </Coquille>
  );
}
